import React, { useEffect, useState, useCallback } from 'react';
import { ListTodo, RefreshCw, CheckCircle2, XCircle, Check, X, CheckCheck, User, Clock, Loader2, CircleDot } from 'lucide-react';
import { getActions, updateActionStatus, SessionAction, ActionStatus } from '../../services/sessionActionsService';

interface ActionsPanelProps {
  sessionId: string;
  onPendingCountChange?: (count: number) => void;
}

type FilterTab = 'all' | ActionStatus;

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const STATUS_CONFIG: Record<ActionStatus, { label: string; pill: string; dot: string }> = {
  pending:   { label: 'Pending',   pill: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-400' },
  accepted:  { label: 'Accepted',  pill: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-500' },
  completed: { label: 'Completed', pill: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  rejected:  { label: 'Rejected',  pill: 'bg-slate-100 text-slate-500',   dot: 'bg-slate-300' },
};

const TABS: { id: FilterTab; label: string }[] = [
  { id: 'all',       label: 'All' },
  { id: 'pending',   label: 'Pending' },
  { id: 'accepted',  label: 'Accepted' },
  { id: 'completed', label: 'Completed' },
  { id: 'rejected',  label: 'Rejected' },
];

function ConfidenceDots({ value }: { value: number }) {
  const level = value >= 0.8 ? 3 : value >= 0.6 ? 2 : 1;
  const color = level === 3 ? 'bg-green-500' : level === 2 ? 'bg-amber-500' : 'bg-slate-400';
  return (
    <span className="inline-flex items-center gap-1 text-slate-400 text-xs">
      <span className="flex gap-0.5">
        {[0, 1, 2].map(i => (
          <span key={i} className={`w-1 h-2.5 rounded-sm ${i < level ? color : 'bg-slate-200'}`} />
        ))}
      </span>
      <span>{Math.round(value * 100)}%</span>
    </span>
  );
}

function ActionRow({
  action,
  onStatusChange,
  updating,
}: {
  action: SessionAction;
  onStatusChange: (id: string, status: ActionStatus) => void;
  updating: boolean;
}) {
  const cfg = STATUS_CONFIG[action.status];
  const isPending = action.status === 'pending';
  const isAccepted = action.status === 'accepted';
  const isRejected = action.status === 'rejected';

  return (
    <div className={`flex flex-col gap-2.5 px-5 py-4 border-b border-slate-100 last:border-0 transition-opacity ${isRejected ? 'opacity-40' : ''}`}>
      {/* Top row: dot + text + status pill */}
      <div className="flex items-start gap-3">
        <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${cfg.dot}`} />
        <p className={`flex-1 text-sm font-semibold text-navy leading-snug ${isRejected ? 'line-through' : ''}`}>
          {action.text}
        </p>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${cfg.pill}`}>
          {cfg.label}
        </span>
      </div>

      {/* Meta row: assignee chip + time + confidence */}
      <div className="flex items-center gap-3 pl-5 flex-wrap">
        {action.assignee && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
            <User size={10} />
            {action.assignee}
          </span>
        )}
        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
          <Clock size={10} />
          {timeAgo(action.detectedAt)}
        </span>
        <ConfidenceDots value={action.confidence} />
      </div>

      {/* Source quote */}
      {action.sourceText && (
        <p className="pl-5 text-xs text-slate-400 italic border-l-2 border-slate-200 leading-relaxed">
          "{action.sourceText}"
        </p>
      )}

      {/* Action buttons */}
      {(isPending || isAccepted) && (
        <div className="flex gap-2 pl-5 pt-0.5">
          {isPending && (
            <>
              <button
                onClick={() => onStatusChange(action.actionId, 'accepted')}
                disabled={updating}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary hover:bg-primary-dark text-white font-semibold transition-colors disabled:opacity-50"
              >
                <Check size={12} />
                Accept
              </button>
              <button
                onClick={() => onStatusChange(action.actionId, 'rejected')}
                disabled={updating}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 font-semibold transition-colors disabled:opacity-50"
              >
                <X size={12} />
                Reject
              </button>
            </>
          )}
          {isAccepted && (
            <button
              onClick={() => onStatusChange(action.actionId, 'completed')}
              disabled={updating}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold transition-colors disabled:opacity-50"
            >
              <CheckCheck size={12} />
              Mark Complete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const ActionsPanel: React.FC<ActionsPanelProps> = ({ sessionId, onPendingCountChange }) => {
  const [actions, setActions] = useState<SessionAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const fetchActions = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await getActions(sessionId);
      setActions(data);
      onPendingCountChange?.(data.filter(a => a.status === 'pending').length);
    } catch {
      // silent fail
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [sessionId, onPendingCountChange]);

  useEffect(() => {
    fetchActions();
    const interval = setInterval(() => fetchActions(true), 60_000);
    return () => clearInterval(interval);
  }, [fetchActions]);

  const handleStatusChange = async (actionId: string, status: ActionStatus) => {
    setUpdatingId(actionId);
    setActions(prev => prev.map(a => a.actionId === actionId ? { ...a, status } : a));
    onPendingCountChange?.(actions.filter(a => a.actionId !== actionId && a.status === 'pending').length);
    try {
      await updateActionStatus(sessionId, actionId, status);
    } catch {
      fetchActions(true);
    } finally {
      setUpdatingId(null);
    }
  };

  const counts = {
    all:       actions.length,
    pending:   actions.filter(a => a.status === 'pending').length,
    accepted:  actions.filter(a => a.status === 'accepted').length,
    completed: actions.filter(a => a.status === 'completed').length,
    rejected:  actions.filter(a => a.status === 'rejected').length,
  };

  const filtered = activeTab === 'all' ? actions : actions.filter(a => a.status === activeTab);

  // Empty state (no actions at all)
  if (!loading && actions.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto p-8">
        <div className="w-full flex flex-col gap-6">
          <header className="flex items-center justify-between border-b border-slate-200 pb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <ListTodo size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-navy">Actions</h1>
                <p className="text-slate-500 text-sm font-medium">AI-detected from session transcript</p>
              </div>
            </div>
            <button
              onClick={() => fetchActions(true)}
              disabled={refreshing}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
            >
              {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              <span>Refresh</span>
            </button>
          </header>
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <ListTodo size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No action items detected yet.</p>
            <p className="text-slate-400 text-xs mt-2">
              Say things like <strong>"remember to ask Patrick about the CFO role"</strong> and actions will appear here automatically.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="w-full flex flex-col gap-6">

        {/* Header */}
        <header className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <ListTodo size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Actions</h1>
              <p className="text-slate-500 text-sm font-medium">AI-detected from session transcript</p>
            </div>
          </div>
          <button
            onClick={() => fetchActions(true)}
            disabled={refreshing}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
          >
            {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </header>

        {/* Loading */}
        {loading ? (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <Loader2 size={32} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-slate-500 text-sm font-medium">Loading actions...</p>
          </div>
        ) : (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total', value: counts.all,       icon: CircleDot,    color: 'text-slate-400' },
                { label: 'Pending',  value: counts.pending,  icon: Clock,        color: 'text-amber-500' },
                { label: 'Done',     value: counts.completed,icon: CheckCircle2, color: 'text-emerald-500' },
                { label: 'Rejected', value: counts.rejected, icon: XCircle,      color: 'text-slate-400' },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex flex-col gap-1 relative overflow-hidden group">
                  <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Icon size={48} className={color} />
                  </div>
                  <p className="text-slate-500 text-sm font-medium uppercase tracking-wide">{label}</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-navy">{value}</p>
                    {label === 'Pending' && value > 0 && (
                      <span className="text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded text-xs font-bold">{value} open</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Tab bar + list */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

              {/* Tab bar */}
              <div className="flex overflow-x-auto border-b border-slate-200 bg-slate-50">
                {TABS.map(tab => {
                  const isActive = activeTab === tab.id;
                  const count = counts[tab.id];
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-5 py-3.5 shrink-0 border-b-2 transition-all text-sm font-bold ${
                        isActive
                          ? 'border-primary bg-white text-navy'
                          : 'border-transparent text-slate-500 hover:text-navy hover:bg-white/60'
                      }`}
                    >
                      {tab.label}
                      {count > 0 && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                          isActive ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-500'
                        }`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Action list */}
              {filtered.length === 0 ? (
                <div className="p-12 text-center">
                  <p className="text-slate-400 text-sm">No {activeTab === 'all' ? '' : activeTab} actions.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filtered.map(action => (
                    <ActionRow
                      key={action.actionId}
                      action={action}
                      onStatusChange={handleStatusChange}
                      updating={updatingId === action.actionId}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActionsPanel;
